import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Avatar } from '../../components/ui/Avatar';
import { Explicit } from '../../components/ui/Explicit';
import { SkeletonRow, SkeletonText } from '../../components/ui/Loader';
import { Drawer } from '../../app/Drawer';
import { DrawerLink } from '../../app/DrawerLink';
import { ApiError, api } from '../../lib/api';
import { Artwork } from '../dashboard/Artwork';
import { duration, rooms } from '../rooms/api';
import type { TrackSummary } from '../tracks/TrackList';
import { PlatformIcon } from './links';
import type { Profile } from './types';
import { useDocumentTitle } from '../../lib/title';

/**
 * An artist, without leaving the page you were on.
 *
 * ## Why this exists at all
 *
 * A host mid-broadcast clicks a name for one of two reasons: to see who sent
 * this, or to find something else of theirs to play. Both of those were a full
 * navigation away from a console with a queue in flight, a chat, and a track
 * playing - and the way back was the browser's back button. This is the same
 * drawer tracks and albums open into, holding the answer to both questions.
 *
 * ## Which is why it can queue
 *
 * `add` and `play next` are the whole point of the tracks list here. Finding an
 * artist and then having to search their track by name in a separate dialog is
 * two steps for something a host does constantly.
 *
 * ## And why those buttons are not always there
 *
 * They appear only when the drawer was opened from the console - which is
 * exactly what `location.state.background` records. Somebody who clicked an
 * artist from the feed gets a profile; somebody who clicked one while running a
 * room gets a profile they can play from. It is also the only honest test
 * available: asking the server whether the viewer has a station would *create*
 * one, because `MyStationAction` creates on first read.
 */
export function ArtistDrawer() {
  const { username = '' } = useParams();
  const location = useLocation();
  const background = (location.state as { background?: { pathname?: string } } | null)?.background;
  const fromConsole = background?.pathname === '/console';

  const { data: profile, isPending, error } = useQuery({
    queryKey: ['profile', username],
    queryFn: () => api.get<Profile>(`/users/${username}`),
  });

  useDocumentTitle(profile ? (profile.display_name || profile.username) : null);

  return (
    <Drawer title={profile?.display_name ?? username} fullPath={`/${username}`}>
      {isPending ? (
        <div className="flex flex-col gap-4 p-5">
          <SkeletonText lines={3} />
          <SkeletonRow />
          <SkeletonRow />
        </div>
      ) : error ? (
        <div className="p-5">
          <Alert tone="error">
            {error instanceof ApiError ? error.message : 'That profile could not be loaded.'}
          </Alert>
        </div>
      ) : profile ? (
        <div className="flex flex-col gap-5 p-5" data-artist-drawer={username}>
          <Header user={profile} />
          <Tracks username={username} queueable={fromConsole} />
        </div>
      ) : null}
    </Drawer>
  );
}

/**
 * Who they are, in as little as will do.
 *
 * No banner, no counts beyond followers, no tabs. This is a glance during a
 * broadcast, and everything omitted is one click away on the full profile.
 */
function Header({ user }: { user: Profile }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <Avatar
          src={user.avatar?.medium ?? null}
          name={user.display_name}
          size={56}
          className="shrink-0"
        />

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <Link
            to={`/${user.username}`}
            className="truncate text-lg font-bold leading-tight text-ink hover:underline"
          >
            {user.display_name}
          </Link>
          <span className="truncate font-mono text-[11px] text-faint">@{user.username}</span>
          <span className="font-mono text-[10px] text-ghost">
            {user.follower_count.toLocaleString()} follower{user.follower_count === 1 ? '' : 's'}
          </span>
        </div>
      </div>

      {user.bio && (
        // Clamped rather than scrolled: the tracks are what this drawer is for,
        // and a long bio would push them under the fold.
        <p className="line-clamp-3 text-[13px] leading-relaxed text-soft">{user.bio}</p>
      )}

      {user.links.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {user.links.map((link) => (
            <a
              key={link.url}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer nofollow"
              title={link.label}
              aria-label={link.label}
              className="flex h-7 w-7 items-center justify-center rounded-[var(--radius-pill)] border border-line text-soft transition-colors hover:border-line-strong hover:text-ink"
            >
              <PlatformIcon platform={link.platform} className="h-3.5 w-3.5" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Their catalogue, queueable.
 *
 * Only what a room can actually take: `PlayableTrack` requires ready, public,
 * released and unheld, and a button that always failed would be worse than an
 * absent one. The count says what was left out rather than silently shrinking
 * the list, because "I know they have eleven tracks" is a thing a host will
 * think.
 */
function Tracks({ username, queueable }: { username: string; queueable: boolean }) {
  const [added, setAdded] = useState<Record<number, 'queued' | 'pinned'>>({});

  const { data, isPending } = useQuery({
    queryKey: ['tracks', username],
    queryFn: () => api.get<{ items: TrackSummary[] }>(`/users/${username}/tracks`),
  });

  const add = useMutation({
    mutationFn: ({ id, pin }: { id: number; pin: boolean }) => rooms.add(id, pin),
    onSuccess: (_result, { id, pin }) =>
      setAdded((current) => ({ ...current, [id]: pin ? 'pinned' : 'queued' })),
  });

  const all = data?.items ?? [];
  const playable = all.filter(
    (track) => track.status === 'ready' && track.visibility === 'public',
  );
  const hidden = all.length - playable.length;

  return (
    <div className="flex flex-col gap-2.5 border-t border-line pt-5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="meta-caps text-faint">tracks</span>
        {hidden > 0 && (
          <span className="font-mono text-[10px] text-ghost">
            {hidden} not public
          </span>
        )}
      </div>

      {add.isError && (
        <Alert tone="error">
          {add.error instanceof ApiError ? add.error.message : 'That could not be added.'}
        </Alert>
      )}

      {isPending ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          <SkeletonRow />
          <SkeletonRow />
        </div>
      ) : playable.length === 0 ? (
        <p className="py-4 text-sm text-soft">Nothing published yet.</p>
      ) : (
        <ul className="flex flex-col gap-2" data-artist-tracks>
          {playable.map((track) => (
            <li
              key={track.id}
              className="flex items-center gap-3 rounded-[var(--radius-card)] border border-line p-2.5"
            >
              <Artwork
                track={{ title: track.title, artwork: null, artwork_small: track.artwork_small ?? null }}
                className="h-10 w-10 shrink-0 rounded-[var(--radius-chip)]"
              />

              <div className="flex min-w-0 flex-1 flex-col">
                <DrawerLink
                  to={`/tracks/${track.id}`}
                  className="truncate text-sm font-medium text-ink hover:underline"
                >
                  {track.explicit && <Explicit className="mr-1.5 align-[-2px]" />}
                  {track.title}
                </DrawerLink>
                <span className="font-mono text-[10px] text-faint">
                  {duration(track.duration_ms)}
                </span>
              </div>

              {queueable && <QueueButtons track={track} state={added[track.id]} add={add} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The two intentions, the same pair the crate offers. */
function QueueButtons({
  track,
  state,
  add,
}: {
  track: TrackSummary;
  state: 'queued' | 'pinned' | undefined;
  add: { isPending: boolean; mutate: (input: { id: number; pin: boolean }) => void };
}) {
  if (state) {
    // Marked rather than removed. A host who added the wrong one needs to see
    // that they did before they add it again.
    return (
      <span
        data-artist-added={track.id}
        className={`shrink-0 font-mono text-[10px] ${state === 'pinned' ? 'text-fresh' : 'text-soft'}`}
      >
        {state === 'pinned' ? 'playing next' : 'in the queue'}
      </span>
    );
  }

  return (
    <div className="flex shrink-0 items-center gap-1.5">
      <button
        type="button"
        data-artist-add={track.id}
        disabled={add.isPending}
        onClick={() => add.mutate({ id: track.id, pin: false })}
        className="rounded-[var(--radius-chip)] border border-line px-2.5 py-1.5 font-mono text-[10px] text-soft transition-colors hover:border-line-strong hover:text-ink"
      >
        add
      </button>
      <button
        type="button"
        title="Host pick — pins above every weight"
        data-artist-pin={track.id}
        disabled={add.isPending}
        onClick={() => add.mutate({ id: track.id, pin: true })}
        className="rounded-[var(--radius-chip)] border border-fresh-edge bg-fresh-wash px-2.5 py-1.5 font-mono text-[10px] text-fresh transition-opacity hover:opacity-80"
      >
        play next
      </button>
    </div>
  );
}
